import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import toastError from "../../errors/toastError";

import HeaderSection from "./HeaderSection";
import ButtonsSection from "./ButtonsSection";
import VariableInserter from "./VariableInserter";
import TemplatePreview from "./TemplatePreview";
import {
  buildTemplatePayload,
  templateToFormState,
  initialFormState,
  extractVarIndices,
} from "./templatePayload";
import {
  validateForm,
  VALID_CATEGORIES,
  MAX_BODY_LENGTH,
  MAX_FOOTER_LENGTH,
} from "./templateValidation";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2.5),
  },
  section: {
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  charCount: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textAlign: "right",
  },
  errors: {
    backgroundColor: theme.palette.error.light + "22",
    border: "1px solid",
    borderColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
  previewWrapper: {
    position: "sticky",
    top: theme.spacing(2),
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

/**
 * Editor de template WhatsApp (Meta) reutilizável para Create/Edit.
 *
 * Props:
 * - mode: "create" | "edit"
 * - whatsappId, templateId (apenas em edit)
 * - initialTemplate (apenas em edit) — objeto vindo da Meta com `components`, `name`, `language`, `category`
 * - redirectAfterSave (default true) — manda para /whatsapp-templates após sucesso
 */
const TemplateForm = ({
  mode = "create",
  whatsappId: initialWhatsappId = "",
  templateId: initialTemplateId = "",
  initialTemplate = null,
  redirectAfterSave = true,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [whatsapps, setWhatsapps] = useState([]);
  const [whatsappId, setWhatsappId] = useState(String(initialWhatsappId || ""));
  const [form, setForm] = useState(() =>
    initialTemplate ? templateToFormState(initialTemplate) : initialFormState()
  );
  const [submitting, setSubmitting] = useState(false);

  const bodyRef = useRef(null);

  const loadWhatsapps = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/");
      const list = Array.isArray(data) ? data : [];
      const oficial = list.filter((w) => w.channel === "whatsapp_oficial");
      setWhatsapps(oficial);
      if (mode === "create" && oficial.length === 1 && !whatsappId) {
        setWhatsappId(String(oficial[0].id));
      }
    } catch (err) {
      toastError(err);
    }
  }, [mode, whatsappId]);

  useEffect(() => {
    loadWhatsapps();
  }, [loadWhatsapps]);

  const validation = useMemo(() => validateForm(form, mode), [form, mode]);

  const bodyIndices = useMemo(
    () => extractVarIndices(form.body?.text || ""),
    [form.body?.text]
  );

  const setBodyText = (text) =>
    setForm((prev) => ({ ...prev, body: { ...prev.body, text } }));
  const setBodyExample = (idx, value) =>
    setForm((prev) => ({
      ...prev,
      body: {
        ...prev.body,
        examples: { ...prev.body.examples, [idx]: value },
      },
    }));

  const setFooterText = (text) =>
    setForm((prev) => ({ ...prev, footer: { ...prev.footer, text } }));
  const setFooterEnabled = (enabled) =>
    setForm((prev) => ({ ...prev, footer: { ...prev.footer, enabled } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!whatsappId && mode === "create") {
      toast.error("Selecione uma conexão API Oficial.");
      return;
    }
    if (!validation.valid) {
      validation.errors.slice(0, 5).forEach((m) => toast.error(m));
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildTemplatePayload(form, mode);
      let response;
      if (mode === "create") {
        response = await api.post(
          `/whatsapp/${whatsappId}/message-templates`,
          payload
        );
      } else {
        response = await api.patch(
          `/whatsapp/${whatsappId}/message-templates/${initialTemplateId}`,
          payload
        );
      }
      if (response.data?.warnings?.length) {
        response.data.warnings.forEach((w) => toast.info(w));
      }
      toast.success(
        mode === "create"
          ? "Template enviado à Meta para análise."
          : "Template atualizado."
      );
      if (redirectAfterSave) {
        history.push("/whatsapp-templates");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Paper className={classes.paper} component="form" onSubmit={handleSubmit}>
          <Typography variant="body2" className={classes.hint} paragraph>
            Templates passam por aprovação da Meta. Use variáveis no formato{" "}
            <strong>{"{{1}}, {{2}}"}</strong> em sequência. Não inicie nem termine
            o corpo com variáveis.
          </Typography>

          {/* Identificação */}
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Identificação</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Conexão API Oficial"
                  value={whatsappId}
                  onChange={(e) => setWhatsappId(e.target.value)}
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  disabled={mode === "edit"}
                >
                  {whatsapps.map((w) => (
                    <MenuItem key={w.id} value={String(w.id)}>
                      {w.name} (#{w.id})
                    </MenuItem>
                  ))}
                </TextField>
                {whatsapps.length === 0 && (
                  <Typography className={classes.hint}>
                    Nenhuma conexão whatsapp_oficial encontrada.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nome interno (único)"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, "");
                    setForm((p) => ({ ...p, name }));
                  }}
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  placeholder="ex.: confirmacao_pedido"
                  disabled={mode === "edit"}
                  error={!!validation.fieldErrors.name}
                  helperText={validation.fieldErrors.name}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Idioma"
                  value={form.language}
                  onChange={(e) => {
                    const language = e.target.value;
                    setForm((p) => ({ ...p, language }));
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={mode === "edit"}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Categoria"
                  value={form.category}
                  onChange={(e) => {
                    const category = e.target.value;
                    setForm((p) => ({ ...p, category }));
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  {VALID_CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!form.allowCategoryChange}
                      onChange={(e) => {
                        const allowCategoryChange = e.target.checked;
                        setForm((p) => ({
                          ...p,
                          allowCategoryChange,
                        }));
                      }}
                      color="primary"
                      disabled={mode === "edit"}
                    />
                  }
                  label="Permitir reclassificação"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Header */}
          <HeaderSection
            form={form}
            setForm={setForm}
            whatsappId={whatsappId}
            fieldError={validation.fieldErrors.header}
          />

          {/* Body */}
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Corpo da mensagem (BODY) *
            </Typography>
            <TextField
              inputRef={bodyRef}
              value={form.body.text}
              onChange={(e) => setBodyText(e.target.value)}
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              size="small"
              required
              inputProps={{ maxLength: MAX_BODY_LENGTH }}
              error={!!validation.fieldErrors.body}
              helperText={validation.fieldErrors.body}
            />
            <Typography className={classes.charCount}>
              {form.body.text.length}/{MAX_BODY_LENGTH}
            </Typography>
            <Box className={classes.toolbar}>
              <VariableInserter
                inputRef={bodyRef}
                value={form.body.text}
                onChange={setBodyText}
                max={10}
              />
            </Box>
            {bodyIndices.map((idx) => (
              <TextField
                key={`bex-${idx}`}
                label={`Exemplo para {{${idx}}}`}
                value={form.body.examples?.[idx] || ""}
                onChange={(e) => setBodyExample(idx, e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                margin="dense"
                required
              />
            ))}
          </Box>

          {/* Footer */}
          <Box className={classes.section}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!form.footer.enabled}
                  onChange={(e) => setFooterEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography className={classes.sectionTitle}>
                  Rodapé (opcional, sem variáveis, máx. {MAX_FOOTER_LENGTH} chars)
                </Typography>
              }
            />
            {form.footer.enabled && (
              <>
                <TextField
                  value={form.footer.text}
                  onChange={(e) =>
                    setFooterText(e.target.value.slice(0, MAX_FOOTER_LENGTH))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                  inputProps={{ maxLength: MAX_FOOTER_LENGTH }}
                  error={!!validation.fieldErrors.footer}
                  helperText={validation.fieldErrors.footer}
                />
                <Typography className={classes.charCount}>
                  {form.footer.text.length}/{MAX_FOOTER_LENGTH}
                </Typography>
              </>
            )}
          </Box>

          {/* Buttons */}
          <ButtonsSection
            form={form}
            setForm={setForm}
            fieldErrors={validation.fieldErrors.buttons}
          />

          {validation.errors.length > 0 && (
            <Box className={classes.errors}>
              {validation.errors.slice(0, 8).map((msg, i) => (
                <Typography key={i} color="error" variant="body2">
                  • {msg}
                </Typography>
              ))}
              {validation.errors.length > 8 && (
                <Typography color="error" variant="body2">
                  …e mais {validation.errors.length - 8}.
                </Typography>
              )}
            </Box>
          )}

          <Divider />

          <Box className={classes.actions}>
            <Button
              type="button"
              onClick={() => history.push("/whatsapp-templates")}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={
                submitting ||
                (mode === "create" && !whatsappId) ||
                !validation.valid
              }
              startIcon={submitting ? <CircularProgress size={16} /> : null}
            >
              {submitting
                ? "Enviando…"
                : mode === "create"
                ? "Submeter à Meta"
                : "Salvar alterações"}
            </Button>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <Box className={classes.previewWrapper}>
          <Typography variant="subtitle2" gutterBottom>
            Pré-visualização
          </Typography>
          <TemplatePreview form={form} />
        </Box>
      </Grid>
    </Grid>
  );
};

export default TemplateForm;
